import { useEffect, useState } from "react";
import { Close, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Slider,
  styled,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { palette } from "../../themes/theme";
import ColorPicker from "../ColorPicker";
import {
  colorSchemeData,
  defaultCrimeRiskLayerData,
  defaultEnvRiskLayerData,
  ENV_RISK_VALUE_STEP,
  formatEnvRiskValue,
  getEnvRangeUpperBound,
  gradeRangesDefaultData,
  normalizeEnvLegendValues,
  rangeDefaultValues,
} from "../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import { updateShadingSettings } from "../../redux/slices/mapSlice";

const getStripedBackground = (colors) => {
  const percentage = 100 / colors.length;
  return `linear-gradient(to right, ${colors
    .map((color, i) => `${color} ${i * percentage}% ${(i + 1) * percentage}%`)
    .join(", ")})`;
};

const hasLegendGap = (legendData, isEnv = false) => {
  if (legendData.length < 2) return false;
  const last = Number(legendData[legendData.length - 1]?.value);
  const secondLast = Number(legendData[legendData.length - 2]?.value);
  return isEnv
    ? Math.abs(secondLast - last - ENV_RISK_VALUE_STEP) > 0.001
    : secondLast - last !== 1;
};

const CrimeRiskSettingModal = ({ isOpen, handleClose, onSave }) => {
  const [showError, setShowError] = useState(false);
  const shadingSettings = useSelector((state) => state.map.shadingSettings);
  const activeEnvType = useSelector((state) => state.map.activeEnvType);

  const [selectedColorScheme, setSelectedColorScheme] = useState(null);
  const [shadingSettingsData, setShadingSettingsData] =
    useState(shadingSettings);
  const [manualLegendBackup, setManualLegendBackup] = useState([]);
  const dispatch = useDispatch();
  const stored = localStorage.getItem("preferedColorPalette");

  const palletteFromLocal = JSON.parse(stored);
  // Preset values for radio buttons
  const [selectedPreset, setSelectedPreset] = useState(null);

  useEffect(() => {
    const settings = activeEnvType
      ? {
          ...shadingSettings,
          legendColorSchemeData: normalizeEnvLegendValues(
            shadingSettings.legendColorSchemeData,
          ),
        }
      : shadingSettings;
    setShadingSettingsData(settings);
    setSelectedColorScheme(shadingSettings.selectedColorScheme);
    // restore saved defaultKey (select respective radio) and apply preset values
    const savedKey = activeEnvType?.includes("NRK")
      ? null
      : palletteFromLocal?.defaultKey || null;
    setSelectedPreset(savedKey);
    if (
      savedKey &&
      !shadingSettings.useGradings &&
      rangeDefaultValues[savedKey]
    ) {
      const preset = rangeDefaultValues[savedKey];
      setShadingSettingsData((prev) => {
        const newLegend = prev.legendColorSchemeData.map((item, i) => ({
          ...item,
          value: preset[i] !== undefined ? String(preset[i]) : item.value,
        }));
        return {
          ...prev,
          legendColorSchemeData: newLegend,
          selectedColorScheme: null,
        };
      });
    }
  }, [shadingSettings, activeEnvType]);

  const updateLegendColorData = (
    newValue,
    dataIndex,
    triggeredByInput = false,
  ) => {
    setShadingSettingsData((prevData) => {
      const legendColorSchemeData = [...prevData.legendColorSchemeData];
      const existingValue = legendColorSchemeData[dataIndex];
      let colorScheme = selectedColorScheme;

      if (
        existingValue.value === newValue.value &&
        !triggeredByInput &&
        existingValue.color === newValue.color
      ) {
        return prevData;
      }

      if (!triggeredByInput) {
        colorScheme = null;
        setSelectedColorScheme(null);
      }

      legendColorSchemeData[dataIndex] = newValue;

      const lastIndex = legendColorSchemeData.length - 1;
      const secondLastIndex = lastIndex - 1;

      if (triggeredByInput) {
        const step = activeEnvType ? ENV_RISK_VALUE_STEP : 1;
        const minLastValue = activeEnvType ? 0 : 1;
        const formatValue = (val) =>
          activeEnvType ? formatEnvRiskValue(val) : val.toString();

        if (dataIndex === lastIndex) {
          const updatedSecondLast = Number(newValue?.value) + step;
          const tempData = { ...legendColorSchemeData[secondLastIndex] };
          const formattedSecondLast = formatValue(updatedSecondLast);
          if (tempData?.value !== formattedSecondLast) {
            legendColorSchemeData[secondLastIndex] = {
              ...tempData,
              value: formattedSecondLast,
            };
          }
        } else if (dataIndex === secondLastIndex) {
          const secondLastValue = Math.max(
            Number(newValue?.value),
            minLastValue,
          );
          const updatedLast = Math.max(secondLastValue - step, minLastValue);

          const tempLastData = { ...legendColorSchemeData[lastIndex] };
          const tempSecondLastData = {
            ...legendColorSchemeData[secondLastIndex],
          };
          const formattedSecondLast = formatValue(secondLastValue);
          const formattedLast = formatValue(updatedLast);

          if (
            tempSecondLastData.value !== formattedSecondLast ||
            tempLastData.value !== formattedLast
          ) {
            legendColorSchemeData[secondLastIndex] = {
              ...tempSecondLastData,
              value: formattedSecondLast,
            };
            legendColorSchemeData[lastIndex] = {
              ...tempLastData,
              value: formattedLast,
            };
          }
        }
      }

      return {
        ...prevData,
        legendColorSchemeData,
        selectedColorScheme: colorScheme,
      };
    });
  };

  const handleShadingOpacityChange = (_event, newValue) => {
    setShadingSettingsData((prevData) => ({
      ...prevData,
      opacity: newValue / 100,
    }));
  };

  useEffect(() => {
    if (shadingSettingsData.useGradings) {
      setManualLegendBackup(shadingSettingsData.legendColorSchemeData);
      setShadingSettingsData((prev) => {
        const updatedShadingSettingsData = gradeRangesDefaultData.map(
          (item, index) => ({
            ...item,
            color:
              colorSchemeData[selectedColorScheme || 4][
                index % colorSchemeData[selectedColorScheme || 4]?.length
              ],
          }),
        );

        return {
          ...prev,
          legendColorSchemeData: updatedShadingSettingsData,
        };
      });
    } else {
      if (manualLegendBackup.length) {
        setShadingSettingsData((prev) => ({
          ...prev,
          legendColorSchemeData: selectedColorScheme
            ? getUpdatedColorScheme(selectedColorScheme, manualLegendBackup)
            : manualLegendBackup,
        }));
      }
    }
  }, [shadingSettingsData.useGradings]);

  const ValidateFields = (item, index) => {
    let errorMsg = "";
    let showRange = "";
    if (+item.value < 0) {
      errorMsg = "Invalid Value";
    }
    const maxValue = activeEnvType ? 10 : 5000;
    if (+item.value > maxValue) {
      errorMsg = activeEnvType
        ? "Should not be greater than 10"
        : "Should not be greater than 5,000";
    }
    if (
      +item.value <=
      +shadingSettingsData.legendColorSchemeData[index + 1]?.value
    ) {
      errorMsg = `value must be higher than ${
        shadingSettingsData.legendColorSchemeData[index + 1]?.value
      }`;
    }
    if (
      +item.value >=
      +shadingSettingsData.legendColorSchemeData[index - 1]?.value
    ) {
      errorMsg += errorMsg
        ? ` and lower than ${
            shadingSettingsData.legendColorSchemeData[index - 1]?.value
          }`
        : `value must be lower than ${
            shadingSettingsData.legendColorSchemeData[index - 1]?.value
          }`;
    }
    if (index === 0) {
      showRange = `or More`;
    } else if (index === shadingSettingsData.legendColorSchemeData.length - 1) {
      if (
        hasLegendGap(shadingSettingsData.legendColorSchemeData, activeEnvType)
      ) {
        errorMsg = "The range has unexpected gap with above value";
      }
      showRange = `or Less`;
    } else {
      const upperValue =
        shadingSettingsData.legendColorSchemeData[index - 1]?.value;
      showRange = activeEnvType
        ? `to ${getEnvRangeUpperBound(upperValue)}`
        : `to ${Number(upperValue) - 1}`;
    }
    return { errorMsg, showRange };
  };

  const handleFieldValueChange = (value, item, idx) => {
    // manual edit should deselect any selected preset
    setSelectedPreset(null);
    const numericValue = Number(value);
    if (numericValue >= 0) {
      updateLegendColorData(
        {
          ...item,
          value,
        },
        idx,
        true,
      );
    } else {
      updateLegendColorData(
        {
          ...item,
          value: activeEnvType ? "0.0" : "0",
        },
        idx,
        true,
      );
    }
  };

  const handleFieldBlur = (item, idx, hasError) => {
    if (activeEnvType) {
      const normalized = formatEnvRiskValue(item.value);
      if (normalized !== item.value) {
        handleFieldValueChange(normalized, item, idx);
      }
    }
    setShowError(hasError ? true : false);
  };

  const updateLegendItemColor = (color, item, idx) => {
    updateLegendColorData(
      {
        ...item,
        color,
      },
      idx,
      false,
    );
  };

  const updateLegendAsPalette = (colors, index) => {
    setSelectedColorScheme(index);
    setSelectedPreset(null);
    const updatedShadingSettingsData =
      shadingSettingsData.legendColorSchemeData.map((item, index) => ({
        ...item,
        color: colors[index % colors.length],
      }));
    setShadingSettingsData((prev) => ({
      ...prev,
      legendColorSchemeData: updatedShadingSettingsData,
    }));
  };

  const handlePresetChange = (event) => {
    const key = event.target.value;
    if (!key) {
      setSelectedPreset(null);
      return;
    }
    const preset = rangeDefaultValues[key];
    setSelectedPreset(key);
    setSelectedColorScheme(null);
    setShadingSettingsData((prev) => {
      const newLegend = prev.legendColorSchemeData.map((item, i) => ({
        ...item,
        value: preset[i] !== undefined ? String(preset[i]) : item.value,
      }));
      return {
        ...prev,
        legendColorSchemeData: newLegend,
        selectedColorScheme: null,
      };
    });
  };

  const getUpdatedColorScheme = (selectedScheme, layerData) => {
    const updatedScheme = layerData.map((item, index) => ({
      ...item,
      color:
        colorSchemeData[selectedScheme][
          index % colorSchemeData[selectedScheme].length
        ],
    }));
    return updatedScheme;
  };

  const handleAddMore = () => {
    setShadingSettingsData((prev) => {
      const oldData = [...prev.legendColorSchemeData];
      const lastIndex = oldData.length - 1;
      const lastValue = Number(oldData[lastIndex]?.value);

      const newData = [...oldData];

      let newValue = activeEnvType ? 1 : 20;

      if (lastValue === 0) {
        // Shift last value to 1, and new one to 0
        newData[lastIndex] = {
          ...oldData[lastIndex],
          value: activeEnvType ? "1.0" : "1",
        };
        newValue = 0;
      } else if (activeEnvType) {
        newValue = Math.max(lastValue - ENV_RISK_VALUE_STEP, 0);
      } else {
        newValue = lastValue >= 25 ? lastValue - 1 : 20;
      }

      newData.push({
        color:
          colorSchemeData[selectedColorScheme ? selectedColorScheme : 0][
            lastIndex + 1
          ],
        value: activeEnvType
          ? formatEnvRiskValue(newValue)
          : newValue.toString(),
        id: `LegendRange${newData.length}`,
      });

      return {
        ...prev,
        legendColorSchemeData: newData,
      };
    });
  };

  const handleDeleteLegend = () => {
    setShadingSettingsData((prev) => {
      const updatedData = prev.legendColorSchemeData.slice(
        0,
        shadingSettingsData.legendColorSchemeData.length - 1,
      );
      return {
        opacity: prev.opacity,
        legendColorSchemeData: updatedData,
      };
    });
  };

  const getRangeValue = (value, index) => {
    let rangeValue = "";
    if (index === 0) {
      rangeValue = `${value}+`;
    } else if (index === 12) {
      rangeValue = "Less than 40";
    } else {
      rangeValue = `${value} - ${Number(
        shadingSettingsData?.legendColorSchemeData[index - 1]?.value - 1,
      )?.toString()}`;
    }
    return rangeValue;
  };

  const handleModalSave = () => {
    const payload = {
      ...shadingSettingsData,
      legendColorSchemeData: activeEnvType
        ? normalizeEnvLegendValues(shadingSettingsData.legendColorSchemeData)
        : shadingSettingsData.legendColorSchemeData,
      selectedColorScheme,
      defaultKey: selectedPreset || null,
    };

    // persist to localStorage
    localStorage.setItem("preferedColorPalette", JSON.stringify(payload));

    // update redux state
    dispatch(updateShadingSettings(payload));

    onSave();
  };

  const toggleUseGrading = (e) => {
    let selectedscheme = null;
    if (e.target.checked) {
      setShowError(false);
      selectedscheme = 4;
    }
    setSelectedColorScheme(selectedscheme);
    setSelectedPreset(null);
    setShadingSettingsData((prev) => {
      return {
        ...prev,
        useGradings: e.target.checked,
        selectedColorScheme: selectedscheme,
      };
    });
  };

  const handleDefaultClick = () => {
    setSelectedPreset(null);
    if (shadingSettingsData.useGradings) {
      updateLegendAsPalette(colorSchemeData[4], 4);
    } else {
      setSelectedColorScheme(null);
      setShadingSettingsData((prev) => ({
        ...prev,
        legendColorSchemeData: activeEnvType
          ? defaultEnvRiskLayerData
          : defaultCrimeRiskLayerData,
        selectedColorScheme: null,
      }));
    }
  };

  let isError = false;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth={true}
      maxWidth={"sm"}
      sx={{
        "& .MuiPaper-root": {
          background: palette.background.light,
          border: `2px solid ${palette.primary.main}`,
          borderRadius: "13px",
        },
      }}
    >
      <DialogTitle sx={{ color: palette.text.dark, fontWeight: "bold" }}>
        Shading Settings
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <Close />
      </IconButton>
      <DialogContent dividers>
        {/* Preset radios below opacity slider */}
        {!activeEnvType && (
          <Box sx={{ mt: 1, mb: 2, display: "flex" }}>
            <Typography sx={{ color: palette.text.dark, mt: 1, mr: 2 }}>
              Crime
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                row
                value={selectedPreset || ""}
                onChange={handlePresetChange}
              >
                <FormControlLabel
                  value="high"
                  control={<Radio />}
                  label="High"
                  disabled={shadingSettingsData.useGradings || activeEnvType}
                />
                <FormControlLabel
                  value="medium"
                  control={<Radio />}
                  label="Medium"
                  disabled={shadingSettingsData.useGradings || activeEnvType}
                />
                <FormControlLabel
                  value="low"
                  control={<Radio />}
                  label="Low"
                  disabled={shadingSettingsData.useGradings || activeEnvType}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            width: "100%",
          }}
        >
          {!activeEnvType ? (
            <FormControlLabel
              control={
                <Switch
                  checked={shadingSettingsData.useGradings}
                  onChange={toggleUseGrading}
                />
              }
              label={"use Grades"}
              disabled={activeEnvType}
            />
          ) : (
            <div/>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={handleDefaultClick}
            sx={{
              borderRadius: "0px",
              fontWeight: "bold",
            }}
          >
            Default
          </Button>
        </Box>
        <Box sx={{ mt: 1, mb: 2 }}>
          {shadingSettingsData.legendColorSchemeData?.map((item, index) => {
            let validate;
            if (!shadingSettingsData.useGradings) {
              validate = ValidateFields(item, index);
              if (validate.errorMsg) {
                isError = true;
              }
            }
            return (
              <LegendItem key={index}>
                <ColorPicker
                  noCheckbox={true}
                  title={shadingSettingsData.useGradings ? item.grade : ""}
                  color={item.color}
                  setColor={(_k, color) =>
                    updateLegendItemColor(color, item, index)
                  }
                  type={"color"}
                />
                {shadingSettingsData.useGradings ? (
                  <Label>{`(${getRangeValue(item.value, index)})`}</Label>
                ) : (
                  <>
                    <TextField
                      value={item.value}
                      onChange={(e) => {
                        handleFieldValueChange(e.target.value, item, index);
                        setShowError(false);
                      }}
                      error={validate.errorMsg}
                      onBlur={() =>
                        handleFieldBlur(item, index, validate.errorMsg)
                      }
                      type="number"
                      inputProps={
                        activeEnvType
                          ? { step: ENV_RISK_VALUE_STEP }
                          : undefined
                      }
                      variant="standard"
                      size="small"
                      sx={{
                        width: "65%",
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                          {
                            display: "none",
                          },
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                      }}
                      slotProps={{
                        input: {
                          style: {
                            color: palette.text.dark,
                            fontSize: "14px",
                          },
                        },
                      }}
                      helperText={validate.errorMsg}
                    />
                    <Label>{validate.showRange}</Label>
                    {index ===
                      shadingSettingsData.legendColorSchemeData.length - 1 &&
                    shadingSettingsData.legendColorSchemeData.length > 4 ? (
                      <IconButton onClick={handleDeleteLegend}>
                        <Delete />
                      </IconButton>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </LegendItem>
            );
          })}
        </Box>
        {!shadingSettingsData.useGradings && (
          <Button
            onClick={handleAddMore}
            disabled={shadingSettingsData.legendColorSchemeData.length >= 13}
            sx={{
              borderRadius: "0px",
              fontWeight: "bold",
              mr: "16px",
            }}
            variant="outlined"
          >
            Add More
          </Button>
        )}

        <Typography
          sx={{ color: palette.text.dark, my: 2, alignItems: "center" }}
        >
          Color Schemes
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mb: 2, width: "100%" }}>
          {colorSchemeData.map((colors, idx) => (
            <PaletteBox
              key={idx}
              colors={colors}
              onClick={() => updateLegendAsPalette(colors, idx)}
              sx={{
                border:
                  selectedColorScheme === idx
                    ? `2px solid ${palette.primary.main}`
                    : "none",
              }}
            />
          ))}
        </Box>
        <Typography sx={{ color: palette.text.dark, mb: 2 }}>
          Opacity
        </Typography>
        <Slider
          min={0}
          step={5}
          max={100}
          defaultValue={shadingSettingsData.opacity * 100}
          valueLabelDisplay="auto"
          onChange={handleShadingOpacityChange}
          sx={{ alignSelf: "center" }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClose}
          sx={{
            borderRadius: "0px",
            fontWeight: "bold",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleModalSave}
          disabled={showError || isError}
          sx={{
            borderRadius: "0px",
            fontWeight: "bold",
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LegendItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 48px;
`;

const PaletteBox = styled(Box)`
  width: 80px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #aaa;
  cursor: pointer;
  background: ${(props) => getStripedBackground(props.colors)};
`;

const Label = styled(Typography)`
  font-size: 14px;
  color: ${(props) => props.theme.palette.text.primary};
`;

export default CrimeRiskSettingModal;
